import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';

import {
  DOCUMENTS_RELEASE_DATE,
  OnboardingTour,
  TOUR_IDS,
  TOUR_PRIORITY,
  type TourStep,
  useOnboardingTour,
} from '@components/OnboardingTour';
import { ME_INCLUDE, useMe } from '@hooks/users';
import useStoreAppConfig from '@hooks/useStoreAppConfig';
import { useMediaQuery, useTheme } from '@mui/material';

import { setSpotlightedNavSection } from '../nav-vertical/navSpotlight.js';
import { DOCUMENTS_NAV_SECTION_ID, NAV_TOUR_ANCHORS } from '../nav-vertical/navTourAnchors.js';
import { useNavConfig } from '../nav-vertical/useNavConfig.js';

import { NotificationBellOnboardingTour } from './NotificationBellOnboardingTour.js';

const DOCUMENTS_TOUR_STEPS: TourStep[] = [
  {
    targetId: NAV_TOUR_ANCHORS.documents,
    title: 'Documents has its own section now',
    body: 'Templates, Media and eDocuments live here, all together in the sidebar.',
    placement: 'right',
  },
  {
    targetId: NAV_TOUR_ANCHORS.edocuments,
    title: 'New: eDocuments',
    body: 'Build eDocuments from your Templates and Media. This list holds every eDocument, and each order also has its own eDocuments tab.',
    placement: 'right',
  },
];

/**
 * Hosts the onboarding tours that belong to the app shell rather than to a page, so they run on
 * whatever route the user lands on. Rendered inside the dashboard layout, next to the sidebar
 * it points at.
 */
export function DashboardOnboardingTours(): ReactElement {
  const theme = useTheme();
  // Below `md` the sidebar is a temporary drawer, closed by default and never force-opened for a
  // tour, so there is nothing to point at. Don't even queue it there: it would take its turn and
  // immediately give it back, delaying the order tours behind it.
  const isSidebarPermanent = useMediaQuery(theme.breakpoints.up('md'));

  const sections = useNavConfig();
  const [{ sidebarCollapsed }, { setSidebarCollapsed }] = useStoreAppConfig();

  // The section is scope-gated, so it is not always on screen. Queueing a tour whose target
  // can never exist would hold up the tours behind it.
  const hasDocumentsSection = useMemo(
    () => sections.some((section) => section.id === DOCUMENTS_NAV_SECTION_ID),
    [sections]
  );

  // Shares QuickUserToggler's `me` cache (same ME_INCLUDE) instead of firing its own read.
  const { normalizedData, isFetching } = useMe({ include: ME_INCLUDE });
  const me = normalizedData && !Array.isArray(normalizedData) ? normalizedData : null;
  const createdAt = typeof me?.createdAt === 'string' ? me.createdAt : undefined;

  // Only users who predate the release get the tour; accounts created on/after never saw the
  // app without the section. An unresolved `me` read is "not decided yet", not "not eligible".
  const isPreReleaseUser = !!createdAt && new Date(createdAt) < DOCUMENTS_RELEASE_DATE;
  const isMeSettled = !!me || !isFetching;

  const tour = useOnboardingTour(TOUR_IDS.documentsNavSection, DOCUMENTS_TOUR_STEPS, {
    ready: isSidebarPermanent && hasDocumentsSection && isPreReleaseUser,
    priority: TOUR_PRIORITY.documentsNavSection,
  });

  const wasSidebarCollapsed = useRef(false);

  // Step 2 points at the eDocuments entry, which only renders while the section is expanded.
  useEffect(() => {
    if (!tour.open) return undefined;
    setSpotlightedNavSection(DOCUMENTS_NAV_SECTION_ID);
    return () => setSpotlightedNavSection(null);
  }, [tour.open]);

  // With the sidebar collapsed to icons the children live in a hover popover instead of the
  // section, so expand it for the duration of the tour and hand it back on close.
  useEffect(() => {
    if (!tour.open || !sidebarCollapsed) return;
    wasSidebarCollapsed.current = true;
    setSidebarCollapsed(false);
  }, [setSidebarCollapsed, sidebarCollapsed, tour.open]);

  // Give the user the collapsed sidebar back once the tour is over; it is their preference, and
  // the tour is the only reason it was expanded.
  const restoreSidebar = useCallback(() => {
    if (!wasSidebarCollapsed.current) return;
    wasSidebarCollapsed.current = false;
    setSidebarCollapsed(true);
  }, [setSidebarCollapsed]);

  const handleClose = useCallback(() => {
    restoreSidebar();
    tour.onClose();
  }, [restoreSidebar, tour]);

  const handleUnavailable = useCallback(() => {
    restoreSidebar();
    tour.onUnavailable();
  }, [restoreSidebar, tour]);

  return (
    <>
      <OnboardingTour
        open={tour.open}
        steps={tour.steps}
        onClose={handleClose}
        onUnavailable={handleUnavailable}
      />
      <NotificationBellOnboardingTour ready={isMeSettled} />
    </>
  );
}

export default DashboardOnboardingTours;
